/**
 * Launch Page
 *
 * Handles redirects from an external PACS or RIS worklist into Ainovis One.
 * Launches are not exclusive to PACS — RIS worklist, FHIRcast and other
 * entries share this same handoff path.
 *
 * Flow:
 * 1. Radiologist opens a study in dcm4chee / RIS worklist and clicks
 *    "Open in Ainovis".
 * 2. The source redirects to: /launch?token=<jwt>&StudyInstanceUID=<uid>&...
 * 3. This page validates the launch token via `POST /api/v1/launch/validate`.
 * 4. If valid, redirects to /reports/v2/edit/draft/<draftId> with study
 *    metadata pre-loaded.
 * 5. If the user is not authenticated, redirects to /login and stores
 *    the launch intent in sessionStorage.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/context/AuthContext';
import { useBrand } from '../../shared/context/BrandContext';
import { ConfirmationModal } from '../../shared/components/ui/ConfirmationModal';
import {
  isInsideElectron,
  tryOpenDesktopApp,
  resetDesktopAttemptFlag,
} from '../../shared/utils/desktopDetection';
import launchService from './services/launchService';

/** Flat info shown in the success card before redirect. */
interface StudyDisplayInfo {
  patientName?: string;
  modality?: string;
  studyDescription?: string;
}

type LaunchState = 'validating' | 'success' | 'error';

/** State of the desktop-app handoff attempt. */
type HandoffState = 'pending' | 'attempting' | 'opened' | 'skipped';

/** Window name used by dcm4chee IID_URL_TARGET so the browser reuses this tab. */
const LAUNCH_WINDOW_NAME = 'AinovisOne';

/**
 * Dónde se recuerda el resultado del canje de identidad, junto al token que lo
 * produjo.
 *
 * El launch token es de un solo uso: el Hub lo gasta al entregar la identidad.
 * Un segundo canje del mismo token no devuelve «esta identidad fue rechazada»,
 * devuelve «no hay identidad» — que es indistinguible de un lanzamiento
 * corriente y significa «sigue con la sesión que hubiera abierta».
 *
 * Y ese segundo canje ocurre con solo recargar la página: los `useRef` que
 * impiden repetirlo no sobreviven al montaje. Sin esto, un F5 convertía un
 * rechazo en una sesión ajena reutilizada, que es justo el riesgo que la
 * comprobación existe para cerrar.
 *
 * Se guarda el token para no aplicar la decisión de un lanzamiento al
 * siguiente, y en `sessionStorage` porque una decisión sobre quién está
 * delante no debe sobrevivir al cierre de la pestaña.
 */
const LAUNCH_IDENTITY_OUTCOME_KEY = 'ainovis_launch_identity_outcome';

type RememberedOutcome = 'session' | 'no-identity' | 'refused';

/** El resultado que ya se obtuvo para este token, si lo hubo. */
function rememberedOutcome(token: string): RememberedOutcome | null {
  try {
    const raw = sessionStorage.getItem(LAUNCH_IDENTITY_OUTCOME_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { token?: string; status?: RememberedOutcome };
    return saved?.token === token && saved.status ? saved.status : null;
  } catch {
    // Un valor corrupto no puede impedir un lanzamiento: se ignora y se canjea.
    return null;
  }
}

function rememberOutcome(token: string, status: RememberedOutcome) {
  try {
    sessionStorage.setItem(LAUNCH_IDENTITY_OUTCOME_KEY, JSON.stringify({ token, status }));
  } catch {
    /* almacenamiento lleno o bloqueado: se seguirá canjeando, como antes */
  }
}

/**
 * Stages shown in the realistic progress bar while the launch is being
 * resolved. They mirror what actually happens server-side during
 * `POST /api/v1/launch/validate` (token validation → study-context fetch
 * from the PACS → patient history → editor prep).
 *
 * The bar is "anchored" to the two real events we observe on the client:
 * the start of validation and the backend response. The intermediate
 * stages advance on a plausible timer so the user perceives progress; if
 * the response arrives early the bar jumps to 100%, and if it takes longer
 * the bar holds on the last stage (spinner) instead of lying at 100%.
 */
const LAUNCH_STAGES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'receiving', label: 'Recibiendo el estudio del PACS' },
  { id: 'credentials', label: 'Validando credenciales de acceso' },
  { id: 'study', label: 'Obteniendo los datos del estudio' },
  { id: 'history', label: 'Cargando el historial del paciente' },
  { id: 'editor', label: 'Preparando el editor de informes' },
];

export default function LaunchPage() {
  const [searchParams] = useSearchParams();
  const launchQuery = searchParams.toString();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  
  const [state, setState] = useState<LaunchState>('validating');
  const [error, setError] = useState<string>('');
  const [displayInfo, setDisplayInfo] = useState<StudyDisplayInfo | null>(null);

  /**
   * Index of the launch stage currently in progress (see `LAUNCH_STAGES`).
   * Counts how many stages have completed; equals `LAUNCH_STAGES.length`
   * once the backend confirms the study is ready.
   */
  const [progressIndex, setProgressIndex] = useState(0);

  /**
   * Desktop-app handoff flow. When the page loads in a browser (not inside
   * Electron), we try to forward the launch to the installed desktop app
   * via `ainovis://launch?...`. The regular web flow only runs once
   * this state settles to `opened` or `skipped`.
   */
  const [handoffState, setHandoffState] = useState<HandoffState>(
    isInsideElectron() ? 'skipped' : 'pending',
  );
  const { brand } = useBrand();

  // ── Unsaved-report guard ──
  // If the radiologist had an unsaved report open, we show a custom
  // Ainovis confirmation modal before proceeding with the new study.
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const unsavedReturnUrlRef = useRef<string | null>(null);
  const unsavedCheckDoneRef = useRef(false);

  // ── Identidad propagada ──
  // El canje se intenta como mucho una vez por carga de la página. El launch
  // token es de un solo uso en el Hub: un segundo intento no solo no ayudaría,
  // sino que llegaría cuando el primero ya gastó el token. El efecto que lo
  // dispara se reejecuta ante varios cambios de estado, y en desarrollo React
  // lo monta dos veces, así que el guardián no es una precaución teórica.
  const identityExchangeTriedRef = useRef<string | null>(null);

  // Si el canje ya ha respondido. No es lo mismo que haberlo intentado, y la
  // diferencia importa: entre una cosa y la otra hay una petición de red por
  // medio, y el efecto que disparó el canje se vuelve a ejecutar durante ese
  // hueco — en desarrollo siempre, porque StrictMode monta la página dos veces.
  //
  // Sin esta marca, esa segunda ejecución se encontraba el guardión de arriba
  // puesto y se iba directa a validar el estudio con la sesión que hubiera
  // abierta, sin esperar a saber quién lanza. Cuando el canje respondía
  // «rechazado» o «otro profesional» ya era tarde: la protección se la saltaba
  // una carrera.
  const identitySettledRef = useRef(false);

  //: Si ya se ha pedido el estudio. Ver `validateAndRedirect`.
  const validationStartedRef = useRef(false);

  // Cuando el lanzamiento venía firmado por un profesional distinto del que
  // tenía la sesión abierta, se cambia de sesión. El cambio no puede ser mudo:
  // quien estaba trabajando aquí tiene que enterarse de que su sesión se ha
  // cerrado, y quien llega, de que entra como él mismo y no como otro.
  const [identitySwitch, setIdentitySwitch] = useState<{
    previous: string | null;
    current: string;
  } | null>(null);

  // Tag this window so dcm4chee's target="AinovisOne" reuses it
  // instead of opening a new tab for every study.
  useEffect(() => {
    window.name = LAUNCH_WINDOW_NAME;
  }, []);

  // ── Desktop-app handoff ────────────────────────────────────────────────
  // Attempted once per launch URL. Inside Electron this is a no-op. In a
  // browser: try `ainovis://` and wait up to 2 s for the OS to activate
  // the desktop app; on timeout fall back to the normal web flow.
  useEffect(() => {
    if (handoffState !== 'pending') return;
    setHandoffState('attempting');

    const query = new URLSearchParams(searchParams);
    tryOpenDesktopApp('launch', query).then((result) => {
      setHandoffState(result === 'opened' ? 'opened' : 'skipped');
    });
  }, [handoffState, searchParams]);

  // ── Auto-close browser tab after successful handoff ────────────────────
  // dcm4chee opens this page in a named window (`target="AinovisOne"`), so
  // `window.close()` is allowed when it was the opener. If the navigator
  // refuses (e.g. direct URL / deep-link from outside PACS), we fall
  // through to a success screen with manual actions.
  useEffect(() => {
    if (handoffState !== 'opened') return;
    const timerId = window.setTimeout(() => {
      try {
        window.close();
      } catch {
        /* noop */
      }
    }, 1500);
    return () => window.clearTimeout(timerId);
  }, [handoffState]);

  // ── Realistic staged progress bar ──────────────────────────────────────
  // While the launch is being validated (web flow, not desktop handoff),
  // advance through the intermediate stages on a plausible timer. The last
  // stage is intentionally NOT auto-completed here — it stays "in progress"
  // until the backend actually responds, so the bar never lies at 100%.
  useEffect(() => {
    if (handoffState !== 'skipped') return;
    if (state !== 'validating') return;
    if (progressIndex >= LAUNCH_STAGES.length - 1) return;

    const delay = progressIndex === 0 ? 300 : 750;
    const timerId = window.setTimeout(() => {
      setProgressIndex((i) => Math.min(i + 1, LAUNCH_STAGES.length - 1));
    }, delay);
    return () => window.clearTimeout(timerId);
  }, [handoffState, state, progressIndex]);

  // When the backend confirms success, complete every stage (bar → 100%).
  // On error, freeze the bar where it is so the failed stage stays visible.
  useEffect(() => {
    if (state === 'success') {
      setProgressIndex(LAUNCH_STAGES.length);
    }
  }, [state]);

  /**
   * Intenta abrir sesión con la identidad que el lanzamiento pueda traer.
   *
   * Se ejecuta solo cuando no hay sesión, y se antepone al formulario de acceso
   * sin sustituirlo: si el canje no prospera —por el motivo que sea— se sigue
   * exactamente el mismo camino de siempre, con el contexto ya guardado justo
   * antes de llamar aquí. Ese camino lleva años funcionando y esta fase no lo
   * reescribe, solo intenta ahorrárselo al radiólogo.
   *
   * Mientras se espera no se cambia de pantalla: la barra de progreso ya está
   * en «Validando credenciales de acceso», que es literalmente lo que ocurre.
   * Introducir aquí una vista intermedia produciría un parpadeo en el caso
   * mayoritario, que es el que no trae identidad y acaba en el formulario.
   *
   * Va declarada antes del efecto que la invoca a propósito: así no se suma a
   * los avisos de acceso anticipado que el fichero ya arrastra.
   */
  const attemptIdentityExchange = async (token: string) => {
    if (identityExchangeTriedRef.current === token) return;
    identityExchangeTriedRef.current = token;
    identitySettledRef.current = false;
    validationStartedRef.current = false;

    const refuse = () => {
      // El lanzamiento venía a nombre de alguien concreto, el Hub verificó su
      // firma, y aun así no podemos darle sesión: no consta, no está
      // verificado, o pertenece a otra cuenta o a otro grupo.
      //
      // Lo que no se puede hacer es seguir. Si hay una sesión abierta en el
      // equipo es de otra persona, y continuar significaría atender a quien
      // está delante con las credenciales de quien no está — el mismo riesgo
      // que el cambio de profesional, entrando por otra puerta. Así que la
      // sesión se cierra antes de ir a ninguna parte.
      if (isAuthenticated) logout();
      navigate('/login', {
        replace: true,
        state: { fromLaunch: true, identityRefused: true },
      });
    };

    // ¿Ya se canjeó este token? Entonces la decisión está tomada y el token
    // gastado: repetir el canje no la recuperaría, la degradaría.
    const recordado = rememberedOutcome(token);
    if (recordado) {
      identitySettledRef.current = true;
      if (recordado === 'refused') {
        refuse();
        return;
      }
      if (isAuthenticated) {
        validateAndRedirect(token);
        return;
      }
      // Hubo sesión y ya no la hay. No se puede volver a canjear, así que el
      // camino de siempre: el formulario, con el estudio esperando.
      navigate('/login', { replace: true, state: { fromLaunch: true } });
      return;
    }

    const outcome = await launchService.exchangeForSession(token);
    rememberOutcome(token, outcome.status);

    // A partir de aquí ya se sabe quién lanza, así que el efecto puede seguir
    // adelante por su cuenta si vuelve a ejecutarse.
    identitySettledRef.current = true;

    if (outcome.status === 'refused') {
      refuse();
      return;
    }

    const session = outcome.status === 'session' ? outcome.session : null;

    if (session && isAuthenticated && user?.userId !== session.user.userId) {
      // Otro profesional. El lanzamiento viene firmado por la Distribuidora,
      // verificado, de un solo uso y con un minuto de vida; la sesión abierta
      // puede llevar horas ahí. Ante la discrepancia manda la aserción, porque
      // es la que dice quién está *ahora* delante del teclado.
      //
      // Continuar con la sesión anterior es el caso que no puede ocurrir: el
      // informe se firma con quien tiene la sesión, así que el estudio acabaría
      // atribuido a un profesional que no lo ha leído. Que sea silencioso es lo
      // que lo hace peligroso — nadie lo detectaría hasta ver la firma.
      //
      // El aviso se guarda antes de cambiar de sesión: `login` provoca un
      // renderizado inmediato y el nombre anterior ya no estaría disponible.
      setIdentitySwitch({
        previous: user?.email ?? null,
        current: session.user.email,
      });
      login(session.token, session.user);
      return;
    }

    if (session && isAuthenticated) {
      // La misma persona que ya tenía sesión. No se toca nada: renovarla no
      // aportaría nada y cerrarla para volver a abrirla solo añadiría formas
      // de fallar.
      validateAndRedirect(token);
      return;
    }

    if (!session) {
      if (isAuthenticated) {
        // El lanzamiento no traía identidad, que es el caso mayoritario y el
        // que lleva años funcionando: un visor que no propaga nada, o la
        // propagación cerrada. Con sesión abierta se sigue exactamente igual
        // que antes de esta fase.
        //
        // Aquí no hay nadie a quien atribuir el lanzamiento, así que tampoco
        // hay discrepancia posible: nada dice que la persona del teclado no
        // sea la de la sesión. Tratarlo como sospechoso echaría del sistema a
        // todo el que lanza desde un visor corriente.
        validateAndRedirect(token);
        return;
      }

      // Mismo destino de siempre. Lo único que se añade es una señal para que el
      // formulario explique por qué se ha llegado hasta él: quien viene de pulsar
      // «abrir en Ainovis» no ha pedido un formulario, y sin una línea que lo
      // sitúe puede creer que la integración se ha roto.
      //
      // El aviso no dice qué ha fallado, y no por prudencia estética: ni el
      // frontend ni el backend lo saben. El Hub funde a propósito en una sola
      // respuesta «no traía identidad», «caducó» y «ya se había gastado», para que
      // este extremo público no sirva de oráculo. De todos modos el motivo no
      // cambiaría nada para el radiólogo: su siguiente paso es el mismo.
      navigate('/login', { replace: true, state: { fromLaunch: true } });
      return;
    }

    // `login` deja la sesión establecida y, al haber un lanzamiento pendiente
    // en sessionStorage, vuelve a esta misma dirección. El efecto de abajo se
    // reejecuta ya con sesión y continúa por el flujo normal de validación.
    login(session.token, session.user);
  };

  useEffect(() => {
    // Wait for auth to finish loading
    if (isLoading) return;
    // Wait until the desktop handoff attempt has settled (or been skipped).
    if (handoffState === 'pending' || handoffState === 'attempting') return;
    // App was activated externally — stay on this tab; no further work.
    if (handoffState === 'opened') return;

    // ── Check for unsaved launch report (only once) ──
    if (!unsavedCheckDoneRef.current) {
      unsavedCheckDoneRef.current = true;
      const raw = localStorage.getItem('ainovis_unsaved_launch_report');
      if (raw) {
        try {
          const { returnUrl } = JSON.parse(raw);
          unsavedReturnUrlRef.current = returnUrl || null;
        } catch { /* ignore malformed */ }
        setShowUnsavedModal(true);
        return; // Wait for user decision before doing anything else
      }
    }

    // If the modal is still open, don't proceed
    if (showUnsavedModal) return;

    const token = searchParams.get('token');

    // F5 — Aqui existia un "lanzamiento directo": sin token, se construia el
    // contexto clinico entero leyendo la barra de direcciones (StudyInstanceUID,
    // PatientID, PatientName, PatientSex, PatientBirthDate, gatewayId y
    // accountCode) y se abria el editor con eso.
    //
    // Se retira. Nadie firmaba esos datos: el paciente que acababa encabezando
    // el informe era el que dijera la URL, y el `gatewayId` con el que luego se
    // entregaba tambien. Un lanzamiento es un hecho que alguien tiene que
    // atestiguar, y el token es esa firma.
    //
    // No quedan visores que dependan de esto: el boton del PACS apunta a
    // `GET /api/v1/gateways/launch/open/{id}`, que acuna el token y redirige
    // aqui con el.
    if (!token) {
      setState('error');
      setError('No se proporcionó token de lanzamiento. Verifique la configuración de su visor o worklist.');
      return;
    }

    // Always persist launch intent BEFORE any auth check or API call.
    // This ensures the launch context survives if a 401 interceptor forces
    // a hard redirect to /login (window.location.href = '/login').
    sessionStorage.setItem('launchToken', token);
    sessionStorage.setItem('launchRedirect', window.location.search);

    // El canje se intenta SIEMPRE, haya sesión o no.
    //
    // Antes solo se intentaba sin sesión, y eso dejaba abierto el fallo grave:
    // en un puesto compartido, un lanzamiento firmado por otro profesional se
    // atendía con la sesión que el anterior se había dejado abierta. La
    // identidad propagada ni se miraba, y el informe se firmaba con quien no lo
    // había leído.
    //
    // Cuesta una petición extra en los lanzamientos que hoy no la hacen. Es un
    // precio razonable: es la única forma de enterarse de que la persona del
    // teclado no es la de la sesión. Y no compromete la validación posterior,
    // que no consume el token —el canje sí, pero una sola vez, y de eso se
    // encarga el guardián.
    if (!identityExchangeTriedRef.current) {
      void attemptIdentityExchange(token);
      return;
    }

    // El canje puede seguir en vuelo. Validar el estudio ahora significaría
    // hacerlo con la sesión equivocada.
    if (!identitySettledRef.current) return;

    validateAndRedirect(token);
    // `user?.userId` no sobra: cuando el lanzamiento cambia de profesional, la
    // sesión pasa de una persona a otra sin que `isAuthenticated` se mueva —
    // era cierto antes y lo sigue siendo. Sin esta dependencia el efecto no se
    // reejecuta, nadie llega a validar el estudio y el lanzamiento se queda
    // parado en el último paso de la barra.
    //
    // `searchParams` tampoco sobra. El botón del PACS reutiliza esta ventana
    // (`target="AinovisOne"`). Si el navegador no recarga y solo cambia la
    // URL, sin esta dependencia el efecto no se reejecuta y el editor se queda
    // con el paciente del lanzamiento anterior.
  }, [isLoading, isAuthenticated, user?.userId, showUnsavedModal, handoffState, launchQuery]);

  /** User chose to continue with the new study → dismiss modal and let the useEffect proceed. */
  const handleUnsavedContinue = () => {
    localStorage.removeItem('ainovis_unsaved_launch_report');
    setShowUnsavedModal(false);
  };

  /** User chose to go back to the unsaved report. */
  const handleUnsavedGoBack = () => {
    localStorage.removeItem('ainovis_unsaved_launch_report');
    const returnUrl = unsavedReturnUrlRef.current;
    setShowUnsavedModal(false);
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
    } else {
      // Fallback: go to reports dashboard
      navigate('/reports', { replace: true });
    }
  };

  /**
   * Validate the JWT launch token with the backend and redirect to report creation.
   *
   * Se ejecuta una sola vez por carga de la página. El efecto que la invoca
   * depende de varios estados y se reejecuta con cada uno; en desarrollo,
   * además, StrictMode lo ejecuta dos veces de entrada. Sin este guardián el
   * estudio se pedía por duplicado, con dos respuestas compitiendo por dejar
   * la pantalla en un estado distinto.
   */
  const validateAndRedirect = async (token: string) => {
    if (validationStartedRef.current) return;
    validationStartedRef.current = true;

    try {
      setState('validating');

      const { payload, studyContext, patientHistory } = await launchService.validateLaunchToken(token);

      if (import.meta.env.DEV) {
      }

      // Extract display info from nested context
      if (studyContext) {
        setDisplayInfo({
          patientName: studyContext.patient?.patientName,
          modality: studyContext.study?.modalitiesInStudy?.[0],
          studyDescription: studyContext.study?.studyDescription,
        });
      }

      // Build the redirect state flattened for the report editor
      const reportCreateState = {
        fromLaunch: true,
        studyInstanceUID: studyContext?.study?.studyInstanceUID || payload.studyInstanceUID,
        accessionNumber: studyContext?.study?.accessionNumber || payload.accessionNumber,
        patientId: studyContext?.patient?.patientId,
        patientName: studyContext?.patient?.patientName,
        patientSex: studyContext?.patient?.sex,
        patientBirthDate: studyContext?.patient?.birthDate,
        modality: studyContext?.study?.modalitiesInStudy?.[0],
        studyDescription: studyContext?.study?.studyDescription,
        reasonForRequestedProcedure: studyContext?.study?.reasonForRequestedProcedure,
        requestedProcedureDescription: studyContext?.study?.requestedProcedureDescription,
        studyDate: studyContext?.study?.studyDate,
        referringPhysician: studyContext?.study?.referringPhysician,
        gatewayId: payload.gatewayId,
        accountCode: payload.accountCode,
        viewerUrl: studyContext?.viewerUrl,
        patientHistory: patientHistory ?? null,
      };

      if (import.meta.env.DEV) {
      }

      setState('success');

      // Navigate to report editor
      setTimeout(() => {
        sessionStorage.removeItem('launchToken');
        sessionStorage.removeItem('launchRedirect');
        navigate('/reports/v2/edit', {
          replace: true,
          state: reportCreateState,
        });
      }, 1000);

    } catch (err: unknown) {
      console.error('🏥 [Launch] Error:', err);
      setState('error');
      const apiErr = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        apiErr?.response?.data?.message || apiErr?.message || 'Error al validar el token de lanzamiento';
      setError(errorMessage);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f8fafc',
      padding: '2rem',
    }}>
      {handoffState === 'attempting' && (
        <div style={{ textAlign: 'center', maxWidth: '420px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e2e8f0',
            borderTopColor: 'var(--primary-color)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
            Abriendo {brand.productName} One...
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Si tienes la app de escritorio instalada se abrirá automáticamente.
          </p>
          <button
            type="button"
            onClick={() => {
              resetDesktopAttemptFlag();
              setHandoffState('skipped');
            }}
            style={{
              background: 'transparent',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              color: '#475569',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Continuar en el navegador
          </button>
        </div>
      )}

      {handoffState === 'opened' && (
        <div style={{ textAlign: 'center', maxWidth: '420px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#22c55e',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            color: 'white',
            fontSize: '24px',
          }}>
            ✓
          </div>
          <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
            {brand.productName} One abierto
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            El estudio se está cargando en la aplicación de escritorio.
            Ya puedes cerrar esta pestaña.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => {
                try { window.close(); } catch { /* noop */ }
              }}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cerrar pestaña
            </button>
            <button
              type="button"
              onClick={() => {
                resetDesktopAttemptFlag();
                setHandoffState('skipped');
              }}
              style={{
                background: 'transparent',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                color: '#475569',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Continuar en el navegador
            </button>
          </div>
        </div>
      )}

      {handoffState === 'skipped' && state === 'validating' && (
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <h2 style={{
            color: '#1e293b',
            marginBottom: '0.25rem',
            textAlign: 'center',
            fontSize: '1.25rem',
          }}>
            Abriendo el estudio en {brand.productName}
          </h2>
          <p style={{
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '1.75rem',
            fontSize: '0.9rem',
          }}>
            Estamos preparando todo, esto tardará solo unos segundos.
          </p>

          {/* Cambio de profesional: la sesión anterior se ha cerrado. */}
          {identitySwitch && (
            <div
              role="status"
              style={{
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                fontSize: '0.85rem',
                color: '#78350f',
                textAlign: 'center',
              }}
            >
              Este estudio se ha abierto como <strong>{identitySwitch.current}</strong>.
              {identitySwitch.previous
                ? <> Se ha cerrado la sesión de <strong>{identitySwitch.previous}</strong> que había abierta en este equipo.</>
                : null}
            </div>
          )}

          {/* Progress bar */}
          {(() => {
            const total = LAUNCH_STAGES.length;
            const pct = Math.min(95, Math.round(((progressIndex + 0.6) / total) * 100));
            return (
              <div style={{
                height: '8px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '999px',
                overflow: 'hidden',
                marginBottom: '1.5rem',
              }}>
                <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, var(--primary-color), var(--primary-color-2))',
                  borderRadius: '999px',
                  transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }} />
              </div>
            );
          })()}

          {/* Stage checklist */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {LAUNCH_STAGES.map((stage, idx) => {
              const isComplete = idx < progressIndex;
              const isActive = idx === progressIndex;
              const isPending = idx > progressIndex;
              return (
                <div
                  key={stage.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    opacity: isPending ? 0.45 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <span style={{
                    flexShrink: 0,
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white',
                    backgroundColor: isComplete ? '#22c55e' : isActive ? 'var(--primary-color)' : '#cbd5e1',
                    transition: 'background-color 0.3s ease',
                  }}>
                    {isComplete ? (
                      '✓'
                    ) : isActive ? (
                      <span style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid rgba(255,255,255,0.5)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }} />
                    ) : (
                      ''
                    )}
                  </span>
                  <span style={{
                    color: isActive ? '#1e293b' : '#475569',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    {stage.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {handoffState === 'skipped' && state === 'success' && (displayInfo || identitySwitch) && (
        <div style={{ textAlign: 'center' }}>
          {identitySwitch && (
            <div role="status" style={{
              backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px',
              padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.85rem',
              color: '#78350f', textAlign: 'center',
            }}>
              Este estudio se ha abierto como <strong>{identitySwitch.current}</strong>.
              {identitySwitch.previous
                ? <> Se ha cerrado la sesión de <strong>{identitySwitch.previous}</strong> que había abierta en este equipo.</>
                : null}
            </div>
          )}
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#22c55e',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            color: 'white',
            fontSize: '24px',
          }}>
            ✓
          </div>
          <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
            Estudio cargado correctamente
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1rem' }}>
            Redirigiendo al editor de informes...
          </p>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'left',
            maxWidth: '400px',
          }}>
            <p style={{ margin: '0.25rem 0', color: '#475569' }}>
              <strong>Paciente:</strong> {displayInfo?.patientName}
            </p>
            <p style={{ margin: '0.25rem 0', color: '#475569' }}>
              <strong>Modalidad:</strong> {displayInfo?.modality}
            </p>
            {displayInfo?.studyDescription && (
              <p style={{ margin: '0.25rem 0', color: '#475569' }}>
                <strong>Descripción:</strong> {displayInfo.studyDescription}
              </p>
            )}
          </div>
        </div>
      )}

      {handoffState === 'skipped' && state === 'error' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            color: 'white',
            fontSize: '24px',
          }}>
            ✕
          </div>
          <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
            Error de lanzamiento
          </h2>
          <p style={{ color: '#ef4444', marginBottom: '1.5rem' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/reports/v2/edit', { replace: true })}
            style={{
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Ir al editor de informes
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Unsaved launch report confirmation modal */}
      <ConfirmationModal
        isOpen={showUnsavedModal}
        title="Informe sin guardar"
        message="Tienes un informe abierto que no ha sido guardado. Si continúas, los cambios no guardados se perderán. ¿Deseas continuar con el nuevo estudio?"
        confirmText="Continuar"
        cancelText="Volver al informe"
        variant="warning"
        onConfirm={handleUnsavedContinue}
        onCancel={handleUnsavedGoBack}
      />
    </div>
  );
}
